// structures.ts — things you decide to put on the ground.
//
// The photovoltaic array used to appear on whatever cell you clicked, because
// selecting a cell and building on it were the same code path. They are not the
// same act. Selecting is a question — what is this ground worth? — and the
// answer is a panel full of numbers. Building is an assertion, and it should
// take a deliberate press.
//
// The distinction matters beyond tidiness: a survey you can run freely over
// fifty cells is a different tool from a survey that litters the claim with
// hardware as you read it. One invites looking around; the other punishes it.
//
// ── Sizing is measured, not chosen ──────────────────────────────────────────
//
// The array's footprint is not a number somebody picked. It is solved from THIS
// cell's measured insolation for a stated output, so the rectangle on the
// ground is the actual area the canon figure costs — on a shaded north slope it
// is visibly bigger than on a prime one. That is the whole reason to draw it at
// all, and any structure added here should earn its footprint the same way.

import type { SubPlot } from './plotField';
import { arrayAreaFor, PV } from './plotField.ts';
import { createStoredRecord } from './storedRecord.ts';

export interface StructureKind {
  id: string;
  name: string;
  /** Short label for the button. */
  short: string;
  /** One line for the panel, explaining what the footprint means. */
  blurb: string;
  /**
   * Half-width of the (square) footprint in metres, from the cell's own
   * measurements. Returns 0 where the ground cannot support it.
   */
  halfWidthM(sub: SubPlot): number;
}

/** Canon: Solar I at Phoenix Lake delivers 4.38 GWh/sol. This is 1 MW of it. */
const REFERENCE_WH_SOL = 24e6;

export const STRUCTURES: Record<string, StructureKind> = {
  solar: {
    id: 'solar',
    name: 'Photovoltaic array',
    short: 'SOLAR ARRAY',
    blurb: 'Sized from this cell’s measured insolation to deliver 1 MW·sol. '
      + 'Poorer ground needs more of it.',
    halfWidthM(sub) {
      if (!(sub.insolationWhSol > 0)) return 0;
      return Math.sqrt(arrayAreaFor(REFERENCE_WH_SOL, sub.insolationWhSol)) / 2;
    },
  },
};

/**
 * A structure standing on a particular piece of ground.
 *
 * The `STRUCT#<cell>#<kind>` row on the server says which structures exist;
 * this store is the read-through cache of that answer (`structureSync.ts`).
 * Where and how big is not on the row: it is resolved from the sub-plot
 * lattice when the plot loads, so an adopted row carries no geometry until
 * then and is not drawn until it does.
 */
export interface Placement {
  kind: string;
  /** H3 cell, so a placement survives a re-bake at a different radius. */
  cell: string;
  at: number;
  /** 0 = solar, 1 = extractor, 2 = refinery, 3 = workshop, 4 = battery,
   * 5 = smelter, 6 = fabricator, 7 = assembler. */
  classId?: number;
  /** The BLDG# row this placement stands for, when a build made it. */
  buildingId?: string;
  /** Plot-local metres, resolved from the lattice. Absent until the plot loads. */
  x?: number;
  z?: number;
  /** Half-width of the footprint in metres, resolved with x and z. */
  halfM?: number;
}

export const CLASS_IDS: Record<string, number> = {
  solar: 0, extractor: 1, refinery: 2, workshop: 3, battery: 4,
  smelter: 5, fabricator: 6, assembler: 7,
};

/**
 * The pad class of a building kind `CLASS_IDS` does not list, so a class the
 * content grows later draws as a building rather than falling to 0 and being
 * painted as an array.
 */
export const UNKNOWN_CLASS_ID = 9;

/** The pad class a placement draws with: the array is 0, a building never is. */
export function padClassOf(p: Pick<Placement, 'kind' | 'classId'>): number {
  if (p.kind === 'solar') return 0;
  return CLASS_IDS[p.kind] ?? (p.classId && p.classId > 0 ? p.classId : UNKNOWN_CLASS_ID);
}

const KEY = 'atlas.structures.v1';

type Store = Record<string, Placement>;

const { load, save } = createStoredRecord<Placement>(KEY);

/**
 * Key a placement by ground, not by lattice coordinate.
 *
 * Same argument the claims use: a (q, r) is relative to one bake, so re-cutting
 * a site at a different radius would move every structure. The cell does not
 * move, because it is a piece of Mars.
 */
function keyOf(cell: string, kind: string): string {
  return `${cell}:${kind}`;
}

/** Test seam only; the write path is structureSync.ts. */
export function place(p: Placement): void {
  const s = load();
  if (p.classId == null) p.classId = CLASS_IDS[p.kind] ?? 0;
  s[keyOf(p.cell, p.kind)] = p;
  save(s);
}

/**
 * Makes the cache agree with the server's answer.
 *
 * A row already here keeps the geometry the lattice resolved for it, because
 * the answer says which structures stand and nothing about where: dropping it
 * would blank every footprint on screen until the plot loaded again.
 */
export function replaceAll(rows: readonly Placement[]): void {
  const was = load();
  const next: Store = {};
  for (const row of rows) {
    const key = keyOf(row.cell, row.kind);
    const had = was[key];
    next[key] = {
      ...row,
      classId: row.classId ?? CLASS_IDS[row.kind] ?? 0,
      ...(row.x === undefined && had?.x !== undefined
        ? { x: had.x, z: had.z, halfM: had.halfM }
        : {}),
    };
  }
  save(next);
}

/** Records what the lattice resolved for a row, once its plot is loaded. */
export function resolve(
  cell: string, kind: string, geom: { x: number; z: number; halfM: number },
): void {
  const s = load();
  const had = s[keyOf(cell, kind)];
  if (!had) return;
  if (had.x === geom.x && had.z === geom.z && had.halfM === geom.halfM) return;
  save({ ...s, [keyOf(cell, kind)]: { ...had, ...geom } });
}

export function has(cell: string, kind: string): boolean {
  return keyOf(cell, kind) in load();
}

/** Everything standing anywhere. Callers filter to the plot they are drawing. */
export function all(): Placement[] {
  return Object.values(load());
}

export { PV, REFERENCE_WH_SOL };
